import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";
import { isCreatorRole } from "../../../utils/projectVisibility";

const FIELD_NAMES = { tarih: "Tarih", saat: "Saat", plaka: "Plaka", personel: "Personel", personelId: "Personel bağlantısı", tesis: "Tesis", aciklama: "Açıklama", kazaBasligi: "Başlık", olayTuru: "Olay türü", isKazasi: "İş kazası işareti", yapilanIs: "Yapılan iş", sonuc: "Sonuç", alinanOnlemler: "Alınan önlemler", kazaFotoURL: "Fotoğraflar", raporURL: "Rapor", evraklar: "Ek evraklar" };
function HistoryValue({ value }) {
  if (value == null || value === "") return <span>—</span>;
  if (typeof value === "boolean") return <span>{value ? "Evet" : "Hayır"}</span>;
  if (value.toDate || value.seconds) return <span>{(value.toDate?.() || new Date(value.seconds * 1000)).toLocaleString("tr-TR")}</span>;
  if (Array.isArray(value)) return <div>{value.map((item, i) => <div key={i}><HistoryValue value={item} /></div>)}</div>;
  if (typeof value === "object") return value.url ? <HistoryValue value={value.url} /> : <span>{JSON.stringify(value)}</span>;
  if (/^https:\/\//.test(value)) return <a href={value} target="_blank" rel="noreferrer">Dosyayı aç</a>;
  return <span>{String(value)}</span>;
}

const KazaDetay = () => {
  const { id } = useParams();
  const [kaza, setKaza] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const { role, userPermissions } = useAuth();
  useEffect(() => onSnapshot(collection(db, "kazalar", id, "history"), (snapshot) => {
    setHistory(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })).sort((a, b) => (b.version || 0) - (a.version || 0)));
    setHistoryError("");
  }, () => setHistoryError("İşlem geçmişi okunamadı. Erişim ve bağlantıyı kontrol edin.")), [id]);

  useEffect(() => {
    const fetchKaza = async () => {
      try {
        const ref = doc(db, "kazalar", id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setKaza(data);
        }
      } catch (error) {
        console.error("⚠️ Veri alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKaza();
  }, [id]);

  const formatDate = (ts) => {
    if (!ts) return "";
    const dateObj = ts.toDate ? ts.toDate() : new Date(ts);
    return dateObj.toLocaleDateString("tr-TR");
  };

  if (loading) return <div style={{ padding: 20 }}>Yükleniyor...</div>;
  if (!kaza) return <div style={{ padding: 20 }}>Kaza bilgisi bulunamadı.</div>;

  return (
    <div style={{ padding: 20 }}>
      <h2>{kaza.kazaBasligi || "Kaza Başlığı"}</h2>
      {(isCreatorRole(role) || userPermissions?.kazatakip?.duzenle === true) && <Link to={`/kaza-takip/kaza-duzenle/${id}`}>Kazayı Düzenle / Evrakları Yönet</Link>}
      <p><strong>Tarih:</strong> {formatDate(kaza.tarih)}</p>
      <p><strong>Saat:</strong> {kaza.saat}</p>
      <p><strong>Olay türü:</strong> {{ TRAFIK: "Trafik kazası", IS_KAZASI: "İş kazası", RAMAK_KALA: "Ramak kala" }[kaza.olayTuru] || "Trafik kazası"}{kaza.isKazasi && kaza.olayTuru !== "IS_KAZASI" ? " / İş kazası" : ""}</p>
      {kaza.tesis && <p><strong>Olay tesisi:</strong> {kaza.tesis}</p>}
      {kaza.yapilanIs && <p><strong>Yapılan iş:</strong> {kaza.yapilanIs}</p>}
      {kaza.sonuc && <p><strong>Sonuç:</strong> {{ BELIRSIZ: "Henüz belirlenmedi", YARALANMA_YOK: "Yaralanma yok", YARALANMA: "Yaralanma", AGIR: "Ağır yaralanma", OLUM: "Ölüm" }[kaza.sonuc]}</p>}
      {kaza.alinanOnlemler && <p><strong>İlk önlemler:</strong> {kaza.alinanOnlemler}</p>}
      <p><strong>Plaka:</strong> {kaza.plaka}</p>
      <p><strong>Personel:</strong> {kaza.personel}</p>
      <p><strong>Açıklama:</strong> {kaza.aciklama}</p>

      <h3>📸 Fotoğraflar</h3>
      {Array.isArray(kaza.kazaFotoURL) && kaza.kazaFotoURL.length > 0 ? (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {kaza.kazaFotoURL.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`kaza-foto-${i}`}
              style={{ width: 200, height: 150, objectFit: "cover", borderRadius: 8 }}
            />
          ))}
        </div>
      ) : (
        <p>Fotoğraf bulunamadı.</p>
      )}

      {kaza.raporURL && (
        <div style={{ marginTop: 20 }}>
          <h3>📄 Kaza Raporu</h3>
          <a href={kaza.raporURL} target="_blank" rel="noopener noreferrer">
            PDF Raporu Görüntüle
          </a>
        </div>
      )}
      {!!kaza.evraklar?.length && <section><h3>Ek evraklar</h3>{kaza.evraklar.map((file) => <p key={file.url}><a href={file.url} target="_blank" rel="noreferrer">{file.name}</a></p>)}</section>}
      <section style={{ marginTop: 24 }}><h3>İşlem geçmişi</h3>{historyError && <p role="alert">{historyError}</p>}{!history.length && !historyError && <p>Henüz işlem geçmişi yok. Eski kayıtların geçmişi ilk düzenlemeden itibaren tutulur.</p>}{history.map((entry) => <details key={entry.id}><summary>{entry.createdAt?.toDate?.().toLocaleString("tr-TR") || "Kaydediliyor"} — {entry.createdBy} — {entry.action === "CREATE" ? "Kayıt oluşturuldu" : "Kayıt güncellendi"}</summary><table style={{ width: "100%", tableLayout: "fixed", overflowWrap: "anywhere" }}><thead><tr><th>Alan</th><th>Önce</th><th>Sonra</th></tr></thead><tbody>{entry.changes?.map((change) => <tr key={change.field}><td>{FIELD_NAMES[change.field] || change.field}</td><td><HistoryValue value={change.before} /></td><td><HistoryValue value={change.after} /></td></tr>)}</tbody></table></details>)}</section>
    </div>
  );
};

export default KazaDetay;
